import { API_BASE_URL } from '../config';
import { registerBrokerBrands } from '../components/vectorDashboard/brokerBranding';

function getAuthHeaders() {
  const raw = localStorage.getItem('risklo_auth_token');
  const headers = { 'Content-Type': 'application/json' };
  if (raw) headers.Authorization = `Bearer ${raw}`;
  return headers;
}

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || `Request failed (${res.status})`);
  }
  return data;
}

export async function fetchVectorDashboardMeta() {
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/meta`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchPerformanceBundle(body) {
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/performance-bundle`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

export async function fetchCalendar(strategy, minDate) {
  const params = new URLSearchParams({ strategy });
  if (minDate) params.set('minDate', minDate);
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/calendar?${params}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchTrades(strategy, minDate) {
  const params = new URLSearchParams({ strategy });
  if (minDate) params.set('minDate', minDate);
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/trades?${params}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchClients(allClients = false) {
  const params = allClients ? '?all=true' : '';
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/clients${params}`, {
    headers: getAuthHeaders(),
  });
  const data = await parseJson(res);
  // Linked brokerages (SnapTrade) carry their own logos.
  registerBrokerBrands(data?.clients);
  return data;
}

export async function fetchClientPerformance(dataSeriesName, dateRange) {
  const params = new URLSearchParams({ dataSeriesName });
  if (typeof dateRange === 'object' && dateRange.start && dateRange.end) {
    params.set('dateRange', 'Custom');
    params.set('start', dateRange.start);
    params.set('end', dateRange.end);
  } else {
    params.set('dateRange', dateRange || '1Y');
  }
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/clients/0/performance?${params}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchClientTrades(dataSeriesName, dateRange = 'All') {
  const params = new URLSearchParams({ dataSeriesName });
  if (typeof dateRange === 'object' && dateRange.start && dateRange.end) {
    params.set('dateRange', 'Custom');
    params.set('start', dateRange.start);
    params.set('end', dateRange.end);
  } else {
    params.set('dateRange', dateRange || 'All');
  }
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/clients/0/trades?${params}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchClientPositions(dataSeriesName, { monthKey = null } = {}) {
  const params = new URLSearchParams({ dataSeriesName });
  if (monthKey) params.set('monthKey', monthKey);
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/clients/0/positions?${params}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchClientSummary(dataSeriesName, dateRange = 'All') {
  const params = new URLSearchParams({ dataSeriesName });
  if (typeof dateRange === 'object' && dateRange.start && dateRange.end) {
    params.set('dateRange', 'Custom');
    params.set('start', dateRange.start);
    params.set('end', dateRange.end);
  } else {
    params.set('dateRange', dateRange || 'All');
  }
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/clients/0/summary?${params}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchAdminAccountsOverview(dateRange = '1Y') {
  const params = new URLSearchParams();
  if (typeof dateRange === 'object' && dateRange.start && dateRange.end) {
    params.set('dateRange', 'Custom');
    params.set('start', dateRange.start);
    params.set('end', dateRange.end);
  } else {
    params.set('dateRange', dateRange || '1Y');
  }
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/admin/accounts-overview?${params}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function brokerSyncCsv(brokerId, csvText) {
  return brokerSync('csv', { broker: brokerId, csv: csvText });
}

export async function brokerSync(path, body = {}) {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/${path}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

/** Refresh every broker linked to the account in one call. */
export async function resyncAllBrokers() {
  return brokerSync('resync-all');
}

/**
 * Resync a single broker for a single account — the account currently on screen.
 * Used for the caller's own account and, for full admins, another user's account.
 */
export async function resyncAccount({ targetUserId, broker }) {
  return brokerSync('resync-account', { targetUserId, broker });
}

export async function fetchClientReportingPrefs() {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/preferences`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function saveDefaultPortfolio(defaultAccountId) {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/preferences`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ defaultAccountId }),
  });
  return parseJson(res);
}

/**
 * Broker session health. `targetUserId` is for elevated viewers looking at
 * someone else's portfolio — read-only, so an admin can tell a client their
 * session lapsed without being able to act on their connection.
 */
export async function fetchConnectionHealth(targetUserId = null) {
  const qs = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/connection-health${qs}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function prepareNinjaTraderOAuth({ frontendUrl, environment = 'live' } = {}) {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/ninjatrader/oauth/prepare`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ frontendUrl, environment }),
  });
  return parseJson(res);
}

export async function fetchNinjaTraderSyncJob(jobId) {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/ninjatrader/sync-job/${encodeURIComponent(jobId)}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchBrokerStatus() {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/status`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

/* SnapTrade (Schwab, Robinhood, Webull…) ------------------------------- */

/** SnapTrade Connection Portal link (valid 5 minutes). `reconnect`: a broken connection's id. */
export async function snaptradeConnectLink({ reconnect = null, broker = null, darkMode = true, returnTo = null } = {}) {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/snaptrade/connect`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reconnect, broker, darkMode, returnTo }),
  });
  return parseJson(res);
}

/** Every brokerage linkable through SnapTrade: { slug, name, logo, crypto, popular, maintenance }. */
/** { available }: false while RiskLo has no production SnapTrade key (any signed-in user). */
export async function fetchSnaptradeAvailability() {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/snaptrade/availability`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchSnaptradeBrokerages() {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/snaptrade/brokerages`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function snaptradeSync() {
  return brokerSync('snaptrade');
}

/** Unlink one brokerage (all its accounts). */
export async function snaptradeRemoveConnection(connectionId) {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/snaptrade/connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

/** Unlink every brokerage and delete the synced data. */
export async function snaptradeDisconnectAll() {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/snaptrade`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function removeBrokerAccount(body) {
  const res = await fetch(`${API_BASE_URL}/api/broker-sync/accounts`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

export async function fetchClientReportingChartColors() {
  const res = await fetch(`${API_BASE_URL}/api/vector-dashboard/chart-colors`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function saveClientReportingChartColors(colors) {
  const res = await fetch(`${API_BASE_URL}/api/admin/client-reporting-chart-colors`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ colors }),
  });
  return parseJson(res);
}
